import React, { useState } from "react";
import { Box, Typography, Button, styled } from "@mui/material";
import { DiamondWithIcon } from "./DiamondFrame";
import { HandDrawnArrow } from "./HandDrawnArrow";
import { DashedConnector } from "./DashedConnector";
import { toPlainText } from "../../../../components/RichText";
import type { SimpleNamedItem } from "../../../types";

interface KeysToSuccessScreenProps {
  keys: SimpleNamedItem[];
  onBack: () => void;
  onNext: () => void;
  isLast?: boolean;
}

const Container = styled(Box, {
  label: "KeysToSuccessContainer",
})(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  alignItems: "center",
  padding: theme.spacing(3, 8, 6),
  maxWidth: 1280,
  width: "100%",
  margin: "0 auto",
}));

const Title = styled(Typography, {
  label: "KeysToSuccessTitle",
})(() => ({
  fontFamily: "Nunito",
  fontWeight: 700,
  fontSize: "32px",
  lineHeight: "48px",
  color: "#1c252e",
  textAlign: "center",
}));

const KeysContainer = styled(Box, {
  label: "KeysContainer",
})(() => ({
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  position: "relative",
}));

const KeyItem = styled(Box, {
  label: "KeyItem",
})(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(2),
  cursor: "pointer",
}));

const KeyLabel = styled(Typography, {
  label: "KeyLabel",
})(() => ({
  fontFamily: "Proxima Nova",
  fontWeight: 700,
  fontSize: "24px",
  lineHeight: "36px",
  color: "#1c252e",
  textAlign: "center",
  width: 346,
}));

const DefinitionBox = styled(Box, {
  label: "DefinitionBox",
})(({ theme }) => ({
  backgroundColor: "#FFD666",
  borderRadius: 16,
  padding: theme.spacing(1, 2),
  width: 706,
  maxWidth: "100%",
  display: "flex",
  flexDirection: "column",
  gap: 4,
  backdropFilter: "blur(30px)",
  overflow: "hidden",
}));

const DefinitionTitle = styled(Typography, {
  label: "DefinitionTitle",
})(() => ({
  fontFamily: "Proxima Nova",
  fontWeight: 700,
  fontSize: "20px",
  lineHeight: "30px",
  color: "#1c252e",
}));

const DefinitionText = styled(Typography, {
  label: "DefinitionText",
})(() => ({
  fontFamily: "Proxima Nova",
  fontWeight: 400,
  fontSize: "16px",
  lineHeight: "24px",
  color: "#1c252e",
}));

const NavContainer = styled(Box, {
  label: "KeysToSuccessNav",
})(() => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
}));

const BackButton = styled(Button, {
  label: "KeysToSuccessBackButton",
})(({ theme }) => ({
  backgroundColor: "rgba(145, 158, 171, 0.2)",
  color: "rgba(145, 158, 171, 0.8)",
  fontFamily: "Proxima Nova",
  fontWeight: 700,
  fontSize: "15px",
  lineHeight: "26px",
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.spacing(1),
  minWidth: 64,
  height: 48,
  textTransform: "none",
  "&:hover": {
    backgroundColor: "rgba(145, 158, 171, 0.3)",
  },
}));

const NextButton = styled(Button, {
  label: "KeysToSuccessNextButton",
})(({ theme }) => ({
  backgroundColor: "#078dee",
  color: "#ffffff",
  fontFamily: "Proxima Nova",
  fontWeight: 700,
  fontSize: "15px",
  lineHeight: "26px",
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.spacing(1),
  minWidth: 64,
  height: 48,
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#0570c4",
  },
}));

const getDescriptionText = (key: SimpleNamedItem): string => {
  if (!key.description) return "";
  if (typeof key.description === "string") {
    return toPlainText(key.description) || key.description;
  }
  return "";
};

const OneKeyLayout = ({
  keys,
  onSelectKey,
}: {
  keys: SimpleNamedItem[];
  onSelectKey: (index: number) => void;
}) => (
  <KeysContainer sx={{ gap: "32px" }}>
    <HandDrawnArrow
      style={{ position: "absolute", left: "20%", top: 100 }}
      rotation={33}
    />
    <KeyItem
      onClick={() => onSelectKey(0)}
      style={{ position: "absolute", left: 400, top: 50 }}
    >
      <DiamondWithIcon iconUrl={keys[0].icon?.url} iconAlt={keys[0].name} />
      <KeyLabel>{keys[0].name}</KeyLabel>
    </KeyItem>
    <HandDrawnArrow
      style={{ position: "absolute", right: "18%", top: 72 }}
      rotation={220}
    />
  </KeysContainer>
);

const TwoKeyLayout = ({
  keys,
  onSelectKey,
}: {
  keys: SimpleNamedItem[];
  onSelectKey: (index: number) => void;
}) => (
  <KeysContainer sx={{ gap: "240px" }}>
    <HandDrawnArrow
      style={{ position: "absolute", left: 49, top: 0 }}
      rotation={0}
      flip
    />
    <KeyItem
      onClick={() => onSelectKey(0)}
      style={{
        position: "absolute",
        left: 100,
        top: 100,
      }}
    >
      <DiamondWithIcon iconUrl={keys[0].icon?.url} iconAlt={keys[0].name} />
      <KeyLabel>{keys[0].name}</KeyLabel>
    </KeyItem>

    <Box
      sx={{
        position: "absolute",
        left: "50%",
        top: 130,
        transform: "translateX(-50%) scaleY(-1) ",
        pointerEvents: "none",
      }}
    >
      <DashedConnector width={370} height={104} />
    </Box>

    <KeyItem
      onClick={() => onSelectKey(1)}
      style={{
        position: "absolute",
        left: 700,
        top: 100,
      }}
    >
      <DiamondWithIcon iconUrl={keys[1].icon?.url} iconAlt={keys[1].name} />
      <KeyLabel>{keys[1].name}</KeyLabel>
    </KeyItem>
    <HandDrawnArrow
      style={{ position: "absolute", right: -10, top: 200 }}
      rotation={170}
      flip
    />
  </KeysContainer>
);

const ThreeKeyLayout = ({
  keys,
  onSelectKey,
}: {
  keys: SimpleNamedItem[];
  onSelectKey: (index: number) => void;
}) => (
  <KeysContainer sx={{ gap: "186px", alignItems: "flex-start" }}>
    <KeyItem
      sx={{ width: 200 }}
      onClick={() => onSelectKey(0)}
      style={{ position: "absolute", left: 0, top: 30 }}
    >
      <DiamondWithIcon iconUrl={keys[0].icon?.url} iconAlt={keys[0].name} />
      <KeyLabel>{keys[0].name}</KeyLabel>
    </KeyItem>

    {/* Dashed connector between cards 1 and 2 */}
    <Box
      sx={{
        position: "absolute",
        left: 200,
        top: 90,

        pointerEvents: "none",
      }}
    >
      <DashedConnector width={254} height={28} />
    </Box>

    <KeyItem
      sx={{ width: 200 }}
      onClick={() => onSelectKey(1)}
      style={{ position: "absolute", left: 440, top: 30 }}
    >
      <DiamondWithIcon iconUrl={keys[1].icon?.url} iconAlt={keys[1].name} />
      <KeyLabel>{keys[1].name}</KeyLabel>
    </KeyItem>

    {/* Dashed S-curve connector between cards 2 and 3 */}
    <Box
      sx={{
        position: "absolute",
        left: 640,
        top: 50,
        transform: "scaleY(-1) rotate(15deg)",
        pointerEvents: "none",
      }}
    >
      <DashedConnector width={249} height={147} />
    </Box>

    <KeyItem
      sx={{ width: 200 }}
      onClick={() => onSelectKey(2)}
      style={{ position: "absolute", left: 880, top: 30 }}
    >
      <DiamondWithIcon iconUrl={keys[2].icon?.url} iconAlt={keys[2].name} />
      <KeyLabel>{keys[2].name}</KeyLabel>
    </KeyItem>
  </KeysContainer>
);

export const KeysToSuccessScreen = ({
  keys,
  onBack,
  onNext,
  isLast,
}: KeysToSuccessScreenProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const selectedKey = keys[selectedIndex];
  const descriptionText = selectedKey ? getDescriptionText(selectedKey) : "";

  const renderLayout = () => {
    if (keys.length === 1)
      return <OneKeyLayout keys={keys} onSelectKey={setSelectedIndex} />;
    if (keys.length === 2)
      return <TwoKeyLayout keys={keys} onSelectKey={setSelectedIndex} />;
    if (keys.length >= 3)
      return (
        <ThreeKeyLayout
          keys={keys.slice(0, 3)}
          onSelectKey={setSelectedIndex}
        />
      );
    return null;
  };

  return (
    <Container>
      <Title>Keys to Success</Title>

      {renderLayout()}

      {descriptionText && (
        <DefinitionBox>
          <DefinitionTitle>Definition</DefinitionTitle>
          <DefinitionText>{descriptionText}</DefinitionText>
        </DefinitionBox>
      )}

      <NavContainer>
        <BackButton variant="contained" onClick={onBack}>
          Back
        </BackButton>
        <NextButton variant="contained" onClick={onNext}>
          {isLast ? "Complete" : "Next"}
        </NextButton>
      </NavContainer>
    </Container>
  );
};
